<template lang="pug">
q-dialog#activity-create-form(
  :value="isCreateAutoActivityFormShownLocal",
  seamless,
  position="right",
  @hide="hideAutoActivityDialog"
)
  q-card.workflow-form__card
    .spinner-container.form-spinner-container(v-if="loading")
      q-spinner(color="primary", size="3em")
    template(v-else)
      .row
        .col-10
          span.text-h6.text-bold.workflow-form__card__title {{ getFormCardTitle }}
        .col-2.workflow-form__card__close-icon
          q-btn(flat, round, icon="close", @click="hideAutoActivityDialog")
      .workflow-form__card__scroll
        .row
          .col-12
            .workflow-form__field
              component(
                :is="getFieldComponent(form.fields[0].type)",
                :field="form.fields[0]",
                :object="localActivityTrigger?.id ? localActivityTrigger : {}",
                :required="form.fields[0].require",
                :workflowId="workflowId",
                :status="status",
                @fieldChange="onFieldChange",
                @disableField="disableField",
                @enableField="enableField"
              )
            .workflow-form__subform
              .workflow-form__field(v-for="(field, i) in form?.fields", :key="`${field?.id}${field.label}`")
                component(
                  :is="getFieldComponent(field.type)",
                  :field="field",
                  :object="localActivityTrigger?.id ? localActivityTrigger : {}",
                  :required="field.require",
                  :workflowId="workflowId",
                  :status="props.status",
                  v-if="isShownFieldAvailable(i)",
                  @fieldChange="onFieldChange",
                  @disableField="disableField",
                  @enableField="enableField"
                )

      .row
        .col-6.workflow-form__card__button
          q-btn.workflow-form__card__button--close(flat, no-caps, @click="hideAutoActivityDialog") {{ workflowConstructorLocales.cancel }}
        .col-6.workflow-form__card__button
          q-btn.workflow-form__card__button--accept(
            flat,
            no-caps,
            :disable="isButtonDisabled",
            @click="updateAutoActivity",
            v-if="activityTrigger?.id"
          ) {{ workflowConstructorLocales.save }}

          q-btn.workflow-form__card__button--accept(
            flat,
            no-caps,
            :disable="isButtonDisabled",
            @click="createAutoActivity",
            v-else
          ) {{ workflowConstructorLocales.save }}
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import WorkflowInputField from "@/components/view/workflows/workflowForms/workflowFormsFields/WorkflowInputField";
import WorkflowFloatField from "@/components/view/workflows/workflowForms/workflowFormsFields/WorkflowFloatField";
import WorkflowSelectField from "@/components/view/workflows/workflowForms/workflowFormsFields/WorkflowSelectField";
import WorkflowCheckboxField from "@/components/view/workflows/workflowForms/workflowFormsFields/WorkflowCheckboxField";
import WorkflowActivitiesValidationFields from "@/components/view/workflows/workflowForms/workflowFormsFields/WorkflowActivitiesValidationFields";
import ColorPicker from "@/components/shared/general_components/ColorPicker";

import { handleError } from "@/services/handleErrors";
import backend from "@/api";
import { workflowConstructorLocales } from "@/services/useLocales";

const props = defineProps({
  /* обект текущего триггера 
    {
      "id":124,
      "title":"123",
      "trigger_type":"max_duration_expired",
      "action_type":"change_status",
      "action_value":{
                      "id":2,
                      "title":
                      "принята"
      }
    }
  */
  activityTrigger: { type: Object, default: () => {} },
  /* булеан общего показа формы */
  isCreateAutoActivityFormShown: { type: Boolean, default: false },
  /* идентификатор текущего воркфлоу */
  workflowId: { type: Number, default: null },
  /* объект, содержащий в себе статус в формате признак: значение 
  {
    "id":1,
    "title":"новая",
    "workflow_id":1,
    "started":true,
    "terminated":false,
    "pause_available":false,
    "accepted":false,
    "percentage_completion":0,
    "template":{"color":"#5b7aff","position":["357","70"]},
    "created_at":"2023-04-25 09:16:43 UTC",
    "updated_at":"2025-05-20 13:46:03 UTC",
    "deleted_at":null,
    "roles":["2","3","4","9","10","11","12","13"],
    "external_id":null,
    "max_duration":0,
    "formated_duration":"0с.",
    "activities":[],
    "transitions":
      [
        {
          "id":1,
          "title":"Принять",
          "old_status":{"id":1,"title":"новая"},
          "new_status":{"id":2,"title":"принята"},
          "form":{
            "id":1,
            "title":"Accept Form",
            "validations":[
              {
                "id":7075,
                "attribute":"executor_id",
                "type":null,
                "options":{},
                "attribute_title":"Исполнитель"
              },
            ]
          },
          "roles":["2","4","10","3","5","9","11","12","13","16"],
          "template":{"color":"#c181ff"},
          "created_at":"2023-04-25 09:16:43 UTC",
          "updated_at":"2025-02-27 11:27:04 UTC"},
        }
      ],
      "days":0,
      "hours":0,
      "minutes":0
    }
  */
  status: { type: Object, default: null },
});

const emit = defineEmits(["updateTransition", "hideDialog"]);

const form = ref();
const currentAutoActivity = ref({ ...props.activityTrigger } || {});
const localActivityTrigger = ref({ ...props.activityTrigger } || {});
const validation = ref([]);
const disabledFields = ref([]);

const loading = ref(true);

const isCreateAutoActivityFormShownLocal = computed(() => {
  return props.isCreateAutoActivityFormShown && !!props.status?.id;
});

const isButtonDisabled = computed(() => !!disabledFields.value.length);

const shownFields = ref([]);

const isShownFieldAvailable = index => {
  return shownFields.value[index] && index !== 0;
};

const formatShownFields = () => {
  return form.value.fields.map(field => {
    const parentField = field.condition_show?.field_id;
    const desiredVal = field.condition_show?.field_value;
    const parentFieldVal = currentAutoActivity.value[parentField] || null;

    return field.condition_show ? parentFieldVal === desiredVal : true;
  });
};

const disableField = event => {
  if (!disabledFields.value.includes(event)) {
    disabledFields.value.push(event);
  }
};

const enableField = event => {
  disabledFields.value = disabledFields.value.filter(field => field !== event);
};

const onFieldChange = event => {
  let val = event.value;

  if (event.name === "action_type") {
    delete localActivityTrigger.value.action_value;
  }

  if (event.name === "action_value") {
    if (Array.isArray(val)) {
      val = { user_ids: val };
    } else {
      val = { status_id: val };
    }
  }

  currentAutoActivity.value[event.name] = val;
  shownFields.value = formatShownFields();
};

const getFieldComponent = type => {
  if (type === "string") {
    return WorkflowInputField;
  } else if (type === "float") {
    return WorkflowFloatField;
  } else if (type === "select") {
    return WorkflowSelectField;
  } else if (type === "boolean") {
    return WorkflowCheckboxField;
  }
};

const hideAutoActivityDialog = () => {
  currentAutoActivity.value = {};
  emit("hideDialog");
};

const createAutoActivity = async () => {
  try {
    const response = await backend.create(`api/v3/statuses/${props.status?.id}/activity_triggers`, null, {
      activity_trigger: { ...currentAutoActivity.value },
    });

    emit("createAutoActivity", response.data);
    emit("hideDialog");
  } catch (e) {
    await handleError(e);
  }
};

const updateAutoActivity = async () => {
  try {
    const response = await backend.update("api/v3/activity_triggers", props.activityTrigger?.id, {
      activity_trigger: { ...currentAutoActivity.value },
    });

    emit("updateAutoActivity", response.data);
    emit("hideDialog");
  } catch (e) {
    await handleError(e);
  }
};

const onAutoActivityValidationChanged = val => {
  validation.value = val;
};

const getFields = async () => {
  try {
    const result = await backend.index(`api/v3/statuses/${props.status?.id}/activity_triggers/form`);
    form.value = result.data;

    shownFields.value = formatShownFields();
  } catch (e) {
    await handleError(e);
  }
};

const getFormCardTitle = computed(() => {
  if (props.activityTrigger?.id) return `${form.value?.form_header?.edit} №${props.activityTrigger?.id}`;
  else return `${form.value?.form_header?.add}`;
});

const initializeForm = () => {
  form.value.fields.forEach(el => {
    if (el.require && !currentAutoActivity.value[el.name]) disableField(el.name);
  });

  if (currentAutoActivity.value?.action_value) {
    let actionValue = currentAutoActivity.value.action_value;

    if (Array.isArray(actionValue)) {
      const arrayOfIds = [];

      for (const elem of actionValue) {
        arrayOfIds.push(elem.id);
      }

      actionValue = { user_ids: arrayOfIds };
    } else {
      const id = actionValue.id;

      actionValue = { status_id: id };
    }

    currentAutoActivity.value.action_value = actionValue;
  }

  loading.value = false;
};

onMounted(async () => {
  await getFields();

  initializeForm();
});
</script>
